import { Card, Progress, Space, Tag, List, theme } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { ChecklistItem } from '@shared/types/work-order-template.types';
import type { ChecklistResults } from '@shared/types/work-schedule.types';

interface ChecklistProgressProps {
  templateItems: ChecklistItem[];
  results?: ChecklistResults;
}

export const ChecklistProgress: React.FC<ChecklistProgressProps> = ({
  templateItems,
  results,
}) => {
  const { token } = theme.useToken();
  const completionRate = results?.completionRate || 0;
  const completedItems = results?.items?.filter(item => {
    if (typeof item.value === 'boolean') return item.value === true;
    return item.value !== null && item.value !== undefined && item.value !== '';
  }) || [];

  const totalItems = templateItems.length;
  const completedCount = completedItems.length;
  const requiredItems = templateItems.filter(item => item.required);
  const completedRequiredItems = completedItems.filter(item =>
    templateItems.find(t => t.id === item.checklistItemId)?.required
  );

  const incompletedRequiredItems = requiredItems.filter(
    reqItem => !completedItems.some(comp => comp.checklistItemId === reqItem.id)
  );

  const getProgressColor = () => {
    if (completionRate === 100) return 'success';
    if (completionRate >= 50) return 'normal';
    return 'exception';
  };

  const allRequiredCompleted = requiredItems.length > 0 && completedRequiredItems.length === requiredItems.length;

  return (
    <Card title="チェックリスト進捗" size="small">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <div style={{ marginBottom: 8 }}>
            <Space>
              <span>全体進捗:</span>
              <Tag color={completionRate === 100 ? 'success' : 'processing'}>
                {completedCount} / {totalItems} 完了
              </Tag>
            </Space>
          </div>
          <Progress
            percent={completionRate}
            status={getProgressColor()}
            strokeColor={completionRate === 100 ? token.colorSuccess : undefined}
          />
        </div>

        {requiredItems.length > 0 && (
          <div>
            <Space>
              <span>必須項目:</span>
              <Tag color={allRequiredCompleted ? 'success' : 'warning'}>
                {completedRequiredItems.length} / {requiredItems.length} 完了
              </Tag>
              {allRequiredCompleted ? (
                <CheckCircleOutlined style={{ color: token.colorSuccess }} />
              ) : (
                <CloseCircleOutlined style={{ color: token.colorWarning }} />
              )}
            </Space>
          </div>
        )}

        {incompletedRequiredItems.length > 0 && (
          <div>
            <div style={{ marginBottom: 8, fontWeight: 'bold', color: token.colorWarning }}>
              未完了の必須項目:
            </div>
            <List
              size="small"
              dataSource={incompletedRequiredItems}
              renderItem={(item) => (
                <List.Item>
                  <Space>
                    <span>#{item.order + 1}</span>
                    <span>{item.title}</span>
                  </Space>
                </List.Item>
              )}
              style={{
                border: `1px solid ${token.colorWarningBorder}`,
                borderRadius: 4,
                padding: '8px',
                backgroundColor: token.colorWarningBg
              }}
            />
          </div>
        )}

        {completionRate === 100 && (
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: token.colorSuccessBg, borderRadius: 4 }}>
            <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: 24, marginRight: 8 }} />
            <span style={{ color: token.colorSuccess, fontWeight: 'bold' }}>
              すべての項目が完了しました
            </span>
          </div>
        )}
      </Space>
    </Card>
  );
};
